import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material'
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline'
import EmailOutlined from '@mui/icons-material/EmailOutlined'
import NotificationsNone from '@mui/icons-material/NotificationsNone'
import PhoneInTalk from '@mui/icons-material/PhoneInTalk'
import SaveOutlined from '@mui/icons-material/SaveOutlined'
import Refresh from '@mui/icons-material/Refresh'
import SmsOutlined from '@mui/icons-material/SmsOutlined'
import VideocamOutlined from '@mui/icons-material/VideocamOutlined'
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined'
import SupportAgent from '@mui/icons-material/SupportAgent'
import EventAvailable from '@mui/icons-material/EventAvailable'
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined'
import PersonAddAlt from '@mui/icons-material/PersonAddAlt'
import MedicalInformationOutlined from '@mui/icons-material/MedicalInformationOutlined'
import SettingsOutlined from '@mui/icons-material/SettingsOutlined'
import WarningAmber from '@mui/icons-material/WarningAmber'
import { toast } from 'react-toastify'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { fetchClinicBrandsWithLocations } from 'src/supportPortal/services/clinicWorkforceService'
import {
  getSupportClinicBrandCommsProfile,
  getSupportPatientCommunicationPolicyOptions,
  saveSupportPatientCommunicationPolicy,
} from 'src/supportPortal/services/clinicBrandCommsConfigService'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const LEVELS = ['metered', 'critical-only', 'no-comms', 'special-block-all']

const LEVEL_META = {
  metered: {
    label: 'Metered',
    tone: 'success',
    summary: 'Allowed patient communications use quota.',
  },
  'critical-only': {
    label: 'Critical only',
    tone: 'warning',
    summary: 'Critical patient communications are allowed.',
  },
  'no-comms': {
    label: 'No routine comms',
    tone: 'default',
    summary: 'Routine patient communications are blocked; video call and payment categories remain allowed.',
  },
  'special-block-all': {
    label: 'Special hard block',
    tone: 'error',
    summary: 'All patient communications are blocked.',
  },
}

const CATEGORY_FALLBACKS = [
  'video-call',
  'video-call-link',
  'video-call-reminder',
  'video-call-aumhum-affiliated',
  'video-call-clinic-affiliated',
  'video-call-doctor',
  'video-call-therapist',
  'video-call-screener',
  'payment',
  'payment-link',
  'payment-success',
  'payment-receipt',
  'booking',
  'clinical-document',
  'clinic-operations',
  'patient-journey',
  'account-onboarding',
  'manual-support',
  'manual-practitioner',
  'uncategorized',
]

const CATEGORY_META = {
  'video-call': { label: 'Video call', group: 'Always allowed', icon: VideocamOutlined },
  'video-call-link': { label: 'Video call link', group: 'Always allowed', icon: VideocamOutlined },
  'video-call-reminder': { label: 'Video call reminder', group: 'Always allowed', icon: VideocamOutlined },
  'video-call-aumhum-affiliated': { label: 'AUMHUM affiliated practitioner', group: 'Always allowed', icon: VideocamOutlined },
  'video-call-clinic-affiliated': { label: 'Clinic affiliated practitioner', group: 'Always allowed', icon: VideocamOutlined },
  'video-call-doctor': { label: 'Doctor video call', group: 'Always allowed', icon: VideocamOutlined },
  'video-call-therapist': { label: 'Therapist video call', group: 'Always allowed', icon: VideocamOutlined },
  'video-call-screener': { label: 'Screener video call', group: 'Always allowed', icon: VideocamOutlined },
  payment: { label: 'Payment', group: 'Always allowed', icon: PaymentsOutlined },
  'payment-link': { label: 'Payment link', group: 'Always allowed', icon: PaymentsOutlined },
  'payment-success': { label: 'Payment success', group: 'Always allowed', icon: PaymentsOutlined },
  'payment-receipt': { label: 'Payment receipt', group: 'Always allowed', icon: PaymentsOutlined },
  booking: { label: 'Booking', group: 'Configurable', icon: EventAvailable },
  'clinical-document': { label: 'Clinical document', group: 'Configurable', icon: MedicalInformationOutlined },
  'clinic-operations': { label: 'Clinic operations', group: 'Configurable', icon: SettingsOutlined },
  'patient-journey': { label: 'Patient journey', group: 'Configurable', icon: AssignmentOutlined },
  'account-onboarding': { label: 'Account onboarding', group: 'Configurable', icon: PersonAddAlt },
  'manual-support': { label: 'Manual support', group: 'Configurable', icon: SupportAgent },
  'manual-practitioner': { label: 'Manual practitioner', group: 'Configurable', icon: MedicalInformationOutlined },
  uncategorized: { label: 'Uncategorized', group: 'Configurable', icon: AssignmentOutlined },
}

const MODE_FIELDS = [
  { key: 'sendWhatApp', label: 'WhatsApp', icon: ChatBubbleOutline, color: '#0f9d58' },
  { key: 'sendSMS', label: 'SMS', icon: SmsOutlined, color: '#7c3aed' },
  { key: 'sendEmail', label: 'Email', icon: EmailOutlined, color: '#ef6c00' },
  { key: 'sendPushNotifications', label: 'Push', icon: NotificationsNone, color: '#1565c0' },
  { key: 'sendCalls', label: 'Calls', icon: PhoneInTalk, color: '#b91c1c' },
]

const DEFAULT_MODE_SETTINGS = {
  sendEmail: true,
  sendWhatApp: true,
  sendCalls: true,
  sendPushNotifications: true,
  sendSMS: true,
}

function isAlwaysAllowedCategory(category) {
  return category === 'video-call'
    || category?.startsWith('video-call-')
    || category === 'payment'
    || category?.startsWith('payment-')
}

function categoryLabel(category) {
  return CATEGORY_META[category]?.label || category
}

function levelLabel(level) {
  return LEVEL_META[level]?.label || level || 'Critical only'
}

function normalizeModeSettings(settings) {
  return MODE_FIELDS.reduce((acc, field) => {
    acc[field.key] = settings?.[field.key] !== false
    return acc
  }, { ...DEFAULT_MODE_SETTINGS })
}

function readStoredBrand() {
  try {
    return JSON.parse(sessionStorage.getItem('supportClinicBrandData') || 'null')
  } catch (e) {
    return null
  }
}

function normalizePolicyMap(policies) {
  const next = {}
  ;(policies || []).forEach((policy) => {
    if (!policy?.category) return
    next[policy.category] = {
      category: policy.category,
      allowed: policy.allowed === true ? true : policy.allowed === false ? false : null,
      metered: policy.metered === true ? true : policy.metered === false ? false : null,
      reason: policy.reason || '',
    }
  })
  return next
}

function buildPolicies(policyMap) {
  return Object.values(policyMap || {})
    .filter((policy) => {
      if (!policy?.category) return false
      return policy.allowed !== null
        || policy.metered !== null
        || Boolean(policy.reason)
    })
    .map((policy) => ({
      category: policy.category,
      allowed: policy.allowed,
      metered: policy.metered,
      reason: policy.reason || null,
    }))
}

function policyValue(policy) {
  if (policy?.allowed === true) return 'allow'
  if (policy?.allowed === false) return 'deny'
  return 'inherit'
}

function optionSortValue(brand) {
  return (brand?.title || brand?.brandName || brand?.slug || '').toLowerCase()
}

export default function ClinicBrandCommunicationPolicyPage() {
  const navigate = useNavigate()
  const { brandSlug } = useParams()
  const [brands, setBrands] = useState([])
  const [selectedBrand, setSelectedBrand] = useState(null)
  const [loadingBrands, setLoadingBrands] = useState(false)
  const [loadingPolicy, setLoadingPolicy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [options, setOptions] = useState({ levels: LEVELS, categories: CATEGORY_FALLBACKS })
  const [level, setLevel] = useState('critical-only')
  const [modeSettings, setModeSettings] = useState(DEFAULT_MODE_SETTINGS)
  const [policyMap, setPolicyMap] = useState({})
  const [lastUpdated, setLastUpdated] = useState('')

  const categories = useMemo(() => {
    const optionCategories = Array.isArray(options?.categories) && options.categories.length > 0
      ? options.categories
      : CATEGORY_FALLBACKS
    return [...new Set([...optionCategories, ...Object.keys(policyMap || {})])]
  }, [options?.categories, policyMap])

  const levels = useMemo(() => {
    const optionLevels = Array.isArray(options?.levels) && options.levels.length > 0
      ? options.levels
      : LEVELS
    return [...new Set(optionLevels)]
  }, [options?.levels])

  const hardBlocked = level === 'special-block-all'
  const selectedLevelMeta = LEVEL_META[level] || LEVEL_META['critical-only']

  useEffect(() => {
    let active = true
    async function loadBrands() {
      setLoadingBrands(true)
      setError('')
      try {
        const nextBrands = await fetchClinicBrandsWithLocations()
        if (!active) return
        const sortedBrands = [...(nextBrands || [])].sort((a, b) =>
          optionSortValue(a).localeCompare(optionSortValue(b)),
        )
        setBrands(sortedBrands)
        const storedBrand = readStoredBrand()
        const nextSelected = sortedBrands.find((brand) => brand?.slug === brandSlug)
          || sortedBrands.find((brand) => brand?.id === storedBrand?.id)
          || sortedBrands[0]
          || null
        setSelectedBrand(nextSelected)
      } catch (loadError) {
        if (active) {
          setError(loadError?.message || 'Unable to load clinic brands')
        }
      } finally {
        if (active) {
          setLoadingBrands(false)
        }
      }
    }
    loadBrands()
    return () => {
      active = false
    }
  }, [brandSlug])

  useEffect(() => {
    let active = true
    async function loadOptions() {
      try {
        const response = await getSupportPatientCommunicationPolicyOptions()
        if (!active || !isCommonResponseSuccessfulV2(response)) return
        const data = getDataMapFromCommonResponse(response)?.patientCommunicationPolicyOptions || {}
        setOptions({
          levels: Array.isArray(data?.levels) && data.levels.length ? data.levels : LEVELS,
          categories: Array.isArray(data?.categories) && data.categories.length ? data.categories : CATEGORY_FALLBACKS,
        })
      } catch (e) {
        if (active) {
          setOptions({ levels: LEVELS, categories: CATEGORY_FALLBACKS })
        }
      }
    }
    loadOptions()
    return () => {
      active = false
    }
  }, [])

  const loadPolicy = useCallback(async () => {
    if (!selectedBrand?.id) {
      return
    }
    setLoadingPolicy(true)
    setError('')
    try {
      const response = await getSupportClinicBrandCommsProfile(selectedBrand.id)
      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error(getMessageMapFromCommonResponse(response) || 'Unable to load communication policy')
      }
      const data = getDataMapFromCommonResponse(response) || {}
      const profile = data.clinicBrandCommsProfile || {}
      const config = profile.config || data['clinic-brand-comms-config'] || {}
      setLevel(config.patientCommunicationLevel || 'critical-only')
      setModeSettings(normalizeModeSettings(config.communicationModesSettings))
      setPolicyMap(normalizePolicyMap(config.patientCommunicationCategoryPolicies))
      setLastUpdated(config.modifiedTime || config.createdTime || '')
    } catch (loadError) {
      setError(loadError?.message || 'Unable to load communication policy')
    } finally {
      setLoadingPolicy(false)
    }
  }, [selectedBrand?.id])

  useEffect(() => {
    loadPolicy()
  }, [loadPolicy])

  const handleBrandChange = (event, brand) => {
    setSelectedBrand(brand)
    if (brand) {
      sessionStorage.setItem('supportClinicBrandData', JSON.stringify(brand))
      navigate(`/support-home/clinics-hospitals/clinics/${brand.slug || brand.id}/communication-policy`, {
        replace: true,
      })
    }
  }

  const updateMode = (key) => (event) => {
    setModeSettings((prev) => ({
      ...prev,
      [key]: event.target.checked,
    }))
  }

  const updateCategoryPolicy = (category) => (event, value) => {
    if (!value) {
      return
    }
    setPolicyMap((prev) => ({
      ...prev,
      [category]: {
        ...(prev?.[category] || { category }),
        category,
        allowed: value === 'allow' ? true : value === 'deny' ? false : null,
      },
    }))
  }

  const handleSave = async () => {
    if (!selectedBrand?.id) {
      toast.error('Select a clinic brand first')
      return
    }
    setSaving(true)
    setError('')
    try {
      const response = await saveSupportPatientCommunicationPolicy({
        clinicBrandId: selectedBrand.id,
        patientCommunicationLevel: level,
        communicationModesSettings: modeSettings,
        patientCommunicationCategoryPolicies: buildPolicies(policyMap),
      })
      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error(getMessageMapFromCommonResponse(response) || 'Unable to save communication policy')
      }
      const savedConfig = getDataMapFromCommonResponse(response)?.['clinic-brand-comms-config'] || null
      if (savedConfig) {
        setLevel(savedConfig.patientCommunicationLevel || level)
        setModeSettings(normalizeModeSettings(savedConfig.communicationModesSettings))
        setPolicyMap(normalizePolicyMap(savedConfig.patientCommunicationCategoryPolicies))
        setLastUpdated(savedConfig.modifiedTime || savedConfig.createdTime || '')
      }
      toast.success('Communication policy saved')
    } catch (saveError) {
      const message = saveError?.message || 'Unable to save communication policy'
      setError(message)
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const formatLastUpdated = (value) => {
    if (!value) return 'Not saved yet'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString()
  }

  return (
    <>
      <SupportHeader heading="Patient Communication Policy" showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  size="small"
                  loading={loadingBrands}
                  options={brands}
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  getOptionLabel={(option) => option?.title || option?.brandName || option?.slug || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Clinic brand"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingBrands ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="flex-end"
                >
                  <Chip
                    color={selectedLevelMeta.tone}
                    label={levelLabel(level)}
                    variant={hardBlocked ? 'filled' : 'outlined'}
                  />
                  <Chip variant="outlined" label={`Updated: ${formatLastUpdated(lastUpdated)}`} />
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={loadPolicy}
                    disabled={!selectedBrand?.id || loadingPolicy || saving}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveOutlined />}
                    onClick={handleSave}
                    disabled={!selectedBrand?.id || loadingPolicy || saving}
                  >
                    Save
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {error ? (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {error}
            </Alert>
          ) : null}

          {hardBlocked ? (
            <Alert
              severity="error"
              icon={<WarningAmber />}
              sx={{ borderRadius: 1, '& .MuiAlert-message': { width: '100%' } }}
            >
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" fontWeight={800}>
                  Special hard block is active
                </Typography>
                <Typography variant="body2">
                  Video call links, reminders, payment links, receipts, and routine patient messages are all blocked for this clinic brand.
                </Typography>
              </Stack>
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={5}>
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    Communication Level
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select value={level} onChange={(event) => setLevel(event.target.value)}>
                      {levels.map((item) => (
                        <MenuItem key={item} value={item}>
                          {levelLabel(item)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    {selectedLevelMeta.summary}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={7}>
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    Communication Modes
                  </Typography>
                  <Grid container spacing={1}>
                    {MODE_FIELDS.map((field) => {
                      const Icon = field.icon
                      return (
                        <Grid item xs={12} sm={6} md={4} key={field.key}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1,
                              borderRadius: 1,
                              borderColor: modeSettings[field.key] ? alpha(field.color, 0.45) : 'divider',
                              bgcolor: modeSettings[field.key] ? alpha(field.color, 0.06) : 'background.paper',
                            }}
                          >
                            <FormControlLabel
                              sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                              labelPlacement="start"
                              control={<Switch checked={Boolean(modeSettings[field.key])} onChange={updateMode(field.key)} />}
                              label={(
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Icon sx={{ color: field.color, fontSize: 18 }} />
                                  <Typography variant="body2" fontWeight={700}>{field.label}</Typography>
                                </Stack>
                              )}
                            />
                          </Paper>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={800}>
                  Category Policy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Always-allowed categories are overridden only by the special hard block.
                </Typography>
              </Box>
              <Divider />
              <Grid container spacing={1.25}>
                {categories.map((category) => {
                  const meta = CATEGORY_META[category] || {}
                  const Icon = meta.icon || AssignmentOutlined
                  const alwaysAllowed = isAlwaysAllowedCategory(category)
                  const value = policyValue(policyMap?.[category])
                  return (
                    <Grid item xs={12} md={6} key={category}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          minHeight: 88,
                          borderColor: alwaysAllowed ? alpha('#0f9d58', 0.25) : 'divider',
                          bgcolor: alwaysAllowed ? alpha('#0f9d58', 0.035) : 'background.paper',
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                              <Icon sx={{ fontSize: 19, color: alwaysAllowed ? '#0f9d58' : 'text.secondary' }} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={800} noWrap>
                                  {categoryLabel(category)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {category}
                                </Typography>
                              </Box>
                            </Stack>
                            {alwaysAllowed ? (
                              <Chip size="small" color="success" variant="outlined" label="Always allowed" />
                            ) : null}
                          </Stack>
                          {alwaysAllowed ? (
                            <Typography variant="caption" color="text.secondary">
                              Blocked only by special hard block.
                            </Typography>
                          ) : (
                            <ToggleButtonGroup
                              exclusive
                              fullWidth
                              size="small"
                              value={value}
                              onChange={updateCategoryPolicy(category)}
                            >
                              <ToggleButton value="inherit">Inherit</ToggleButton>
                              <ToggleButton value="allow">Allow</ToggleButton>
                              <ToggleButton value="deny">Deny</ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  )
                })}
              </Grid>
            </Stack>
          </Paper>

          {loadingPolicy ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
              }}
            >
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Loading policy
              </Typography>
            </Paper>
          ) : null}
        </Stack>
      </Box>
    </>
  )
}
